import type React from "react";
import { useState } from "react";
import { Button } from "semantic-ui-react";
import { useUI } from "services/UI";
import { USER_MSG, getTranslatedUserMsg } from "strings";
import { useT } from "i18n";

export interface ConfirmRejectAlertProps {
  isOpen: boolean;
  description: string;
  onClose: () => void;
  onConfirm: (message: string) => void;
}

export default function ConfirmRejectAlert({
  isOpen = false,
  description,
  onClose,
  onConfirm,
}: ConfirmRejectAlertProps): React.ReactElement {
  const t = useT();
  const { showError } = useUI();
  const [message, setMessage] = useState("");

  if (!isOpen) return <></>;

  const handleConfirm = () => {
    if (!message.trim()) {
      showError(getTranslatedUserMsg(t, "ERROR_SCAN_REJECT_EMPTY_REASON"));
      return;
    }
    onConfirm(message);
    setMessage(""); // Reset message after confirm
  };

  const handleClose = () => {
    setMessage(""); // Reset message on close
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "4px",
          width: "500px",
          height: "auto",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          style={{
            padding: "12px",
            borderBottom: "1px solid #e1e5e9",
            fontSize: "15px",
            fontWeight: "700",
            textAlign: "left",
          }}
        >
          {t("component.confirmRejectAlert.warning", "Warning!")}
        </div>

        <div
          style={{
            padding: "12px 12px 20px",
            flex: 1,
            overflow: "auto",
            fontSize: "15px",
            textAlign: "left",
            lineHeight: "1.5em",
          }}
        >
          {description}
        </div>

        <div style={{ padding: "0 12px 12px 12px" }}>
          <textarea
            placeholder={t(
              "component.confirmRejectAlert.reasonForRejection",
              "Reason for rejection",
            )}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
            rows={4}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #e1e5e9",
              borderRadius: "4px",
              fontSize: "14px",
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
        </div>

        <div
          style={{
            padding: "12px 12px 12px",
            borderTop: "1px solid #e1e5e9",
            display: "flex",
            justifyContent: "flex-end",
            gap: "6px",
          }}
        >
          <Button
            basic
            content={t("component.common.cancel", "Cancel")}
            onClick={handleClose}
          />
          <Button
            primary
            content={t("component.confirmRejectAlert.submit", "Submit")}
            onClick={handleConfirm}
          />
        </div>
      </div>
    </div>
  );
}
